import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { getSlidesTemplatePack } from '../decks/getSlidesTemplatePack';
import { buildEnhancedSlides } from '../slides/buildEnhancedSlides';
import './PowerpointView.css';

function createLegacyParagraphs(text, seed) {
  if (!text) return [];
  const t = String(text).trim();
  if (!t) return [];
  const sentences = t.match(/[^.!?]+[.!?]+/g) || [t];

  // Deterministic pseudo-random from seed (so paragraphs stay stable per slide)
  let state = (typeof seed === 'number' ? seed : 0) >>> 0;
  const rand = () => {
    state = (1664525 * state + 1013904223) >>> 0;
    return state / 0xffffffff;
  };

  const paragraphs = [];
  let current = [];
  for (let i = 0; i < sentences.length; i++) {
    current.push(String(sentences[i]).trim());
    if (current.length >= 2 && (i === sentences.length - 1 || rand() > 0.5)) {
      paragraphs.push(current.join(' '));
      current = [];
    }
  }
  if (current.length) paragraphs.push(current.join(' '));
  return paragraphs;
}

function createParagraphs(text, seed) {
  if (!text) return [];
  const t = String(text).trim();
  if (!t) return [];

  // Same behavior as SlidesView: if explicit blank lines exist, honor them.
  if (/\n\n+/.test(t)) {
    return t
      .split(/\n\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Otherwise fall back to the same deterministic sentence grouping method.
  return createLegacyParagraphs(t, seed);
}

function parseBulletContent(text) {
  if (!text) return { type: 'text', items: [] };
  const lines = String(text).split(/\n+/).map(s => s.trim()).filter(Boolean);
  
  const bulletPattern = /^[•\-→]\s+(.+)$/;
  const bullets = [];
  const nonBullets = [];
  
  lines.forEach(line => {
    const match = line.match(bulletPattern);
    if (match) {
      bullets.push(match[1]);
    } else {
      nonBullets.push(line);
    }
  });
  
  if (bullets.length > 0) {
    return { type: 'bullets', items: bullets, prefix: nonBullets };
  }
  return { type: 'text', items: lines };
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function getExcerpt(text, maxLen) {
  const t = text == null ? '' : String(text).trim();
  if (!t) return '';
  const compact = t.replace(/\s+/g, ' ').trim();
  if (compact.length <= maxLen) return compact;
  return `${compact.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

function getVideoUrlFromImageUrl(url) {
  if (!url) return '';
  const s = String(url);
  if (/\.mp4(\?|#|$)/i.test(s)) return s;
  if (/\.(jpe?g|png|webp)(\?|#|$)/i.test(s)) return s.replace(/\.(jpe?g|png|webp)(\?|#|$)/i, '.mp4$2');
  return `${s}.mp4`;
}

function normalizeCountermeasuresActions(countermeasures) {
  if (!countermeasures || !Array.isArray(countermeasures.actions)) return [];
  return countermeasures.actions
    .map((a) => {
      if (a == null) return null;
      if (typeof a === 'string') return a;
      if (typeof a === 'object') {
        const label = a.label ? String(a.label) : '';
        const odds = a.odds ? String(a.odds) : '';
        const agent = a.agent ? String(a.agent) : '';
        const brief = a.brief ? String(a.brief) : '';
        const action = a.action ? String(a.action) : '';
        const combined = [label, odds ? `(${odds})` : '', agent ? `→ ${agent}` : '', brief ? `→ ${brief}` : '', action].filter(Boolean);
        return combined.join(' ').replace(/\s+/g, ' ').trim();
      }
      return String(a);
    })
    .filter(Boolean);
}

function buildTextPages({ text, maxParasPerPage, maxCharsPerPage, seed }) {
  const paras = createParagraphs(text, seed);
  if (paras.length === 0) return [[]];

  const pages = [];
  let cur = [];
  let curLen = 0;

  const push = () => {
    if (cur.length === 0) return;
    pages.push([...cur]);
    cur = [];
    curLen = 0;
  };

  paras.forEach((p) => {
    const nextLen = curLen + (cur.length ? 2 : 0) + p.length;
    const wouldOverflowChars = maxCharsPerPage && nextLen > maxCharsPerPage && cur.length > 0;
    const wouldOverflowParas = maxParasPerPage && cur.length >= maxParasPerPage;
    if (wouldOverflowChars || wouldOverflowParas) push();
    cur.push(p);
    curLen = curLen + (cur.length > 1 ? 2 : 0) + p.length;
  });

  push();
  return pages.length ? pages : [[]];
}

function formatTextWithBold(text) {
  return text.replace(/(\d+%|\d+\.\d+%|£[\d,]+|\$[\d,]+)/g, '<strong>$1</strong>');
}

function normalizeHtmlToText(html) {
  if (!html) return '';
  const raw = String(html);

  try {
    if (typeof DOMParser !== 'undefined') {
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      const blocks = Array.from(doc.body.querySelectorAll('p, li, h1, h2, h3, h4'));
      if (blocks.length > 0) {
        const lines = [];
        blocks.forEach((el) => {
          const t = el.textContent ? String(el.textContent).trim() : '';
          if (!t) return;
          if (String(el.tagName).toUpperCase() === 'LI') lines.push(`→ ${t}`);
          else lines.push(t);
        });
        return lines.join('\n');
      }
      return doc.body.innerText || doc.body.textContent || '';
    }
  } catch (e) {
    // fall through to regex-based fallback
  }

  return raw
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/p\s*>/gi, '\n\n')
    .replace(/<\s*\/li\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function getParagraphSeed(page) {
  const itemIndex = page && typeof page.itemIndex === 'number' ? page.itemIndex : 0;
  const plateIndex = page && typeof page.plateIndex === 'number' ? page.plateIndex : 0;
  const plate = page && page.plate ? String(page.plate) : '';

  let plateCode = 0;
  for (let i = 0; i < plate.length; i++) plateCode = (plateCode * 31 + plate.charCodeAt(i)) >>> 0;

  return ((itemIndex * 1000003) ^ (plateIndex * 9176) ^ plateCode) >>> 0;
}

function buildPagesFromSlides({ slides }) {
  const pages = [];

  slides.forEach((item, itemIndex) => {
    const base = {
      item,
      itemIndex,
      sector: item && item.sector ? item.sector : '',
      sectorId: item && item.sectorId ? item.sectorId : '',
      sectorDisplayName: item && item.sectorDisplayName ? item.sectorDisplayName : '',
      img: item && item.img ? item.img : '',
      title: item && item.title ? item.title : '',
      type: item && item.type ? item.type : '',
      template: item && item.template ? item.template : ''
    };

    if (item && item.type === 'prediction') {
      const baselineSeed = itemIndex * 1000 + 1;
      const baselinePages = buildTextPages({ text: item.story || '', maxParasPerPage: 3, maxCharsPerPage: 900, seed: baselineSeed });
      baselinePages.forEach((paragraphs, idx) => {
        pages.push({ ...base, kind: 'PREDICTION', plate: 'BASELINE', plateIndex: idx, paragraphs, html: null, actions: null });
      });

      if (item && item.signal_validation) {
        pages.push({ ...base, kind: 'PREDICTION', plate: 'SIGNAL', text: '', html: String(item.signal_validation), actions: null });
      }

      if (item && item.mitigated_story) {
        const mitigatedSeed = itemIndex * 1000 + 2;
        const mitigatedPages = buildTextPages({ text: item.mitigated_story, maxParasPerPage: 3, maxCharsPerPage: 900, seed: mitigatedSeed });
        mitigatedPages.forEach((paragraphs, idx) => {
          pages.push({ ...base, kind: 'PREDICTION', plate: 'MITIGATED', plateIndex: idx, paragraphs, html: null, actions: null });
        });
      }

      const actions = normalizeCountermeasuresActions(item && item.countermeasures ? item.countermeasures : null);
      if (actions.length > 0) {
        pages.push({ ...base, kind: 'PREDICTION', plate: 'ACTIONS', text: '', html: null, actions });
      }

      return;
    }

    if (item.type === 'global') {
      pages.push({ ...base, kind: 'GLOBAL', plate: 'TITLE', text: item.story || '', html: null, actions: null });
      return;
    }

    if (item.type === 'landing') {
      pages.push({ ...base, kind: 'SECTOR', plate: 'LANDING', text: item.story || '', html: null, actions: null });
      return;
    }

    if (item.type === 'model') {
      pages.push({ ...base, kind: 'SECTOR', plate: 'MODEL', text: item.story || '', html: null, actions: null });
      return;
    }

    if (item.type === 'cta') {
      pages.push({ ...base, kind: 'SECTOR', plate: 'CTA', text: '', html: null, actions: null, cta: item.cta || null, contact: item.contact || '' });
      return;
    }

    pages.push({ ...base, kind: String(item.type || '').toUpperCase(), plate: 'PAGE', text: item.story || '', html: null, actions: null });
  });

  return pages;
}

function PowerpointView({ deckId, sector, dataState, assetsBaseUrl, registerNavHandlers, motionDisabled }) {
  const sectorParam = sector ? String(sector).toLowerCase() : null;
  const contentScrollRef = useRef(null);

  const templatePack = useMemo(() => getSlidesTemplatePack(deckId), [deckId]);

  const slides = useMemo(() => {
    if (!dataState || dataState.status !== 'ready') return [];
    const root = dataState.data ? dataState.data.root : null;
    const countermeasures = dataState.data ? dataState.data.countermeasures : null;
    const templateSequence = dataState.data ? dataState.data.templateSequence : null;
    return buildEnhancedSlides({ root, countermeasures, sectorParam, templateSequence });
  }, [dataState, sectorParam]);

  const pages = useMemo(() => buildPagesFromSlides({ slides }), [slides]);

  const [index, setIndex] = useState(0);
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [sectorParam, deckId]);

  useEffect(() => {
    const el = contentScrollRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [index]);

  const page = pages.length ? pages[clamp(index, 0, pages.length - 1)] : null;

  const findSectorLandingIndex = useCallback(
    (targetSector) => {
      if (!Array.isArray(pages) || pages.length === 0) return 0;
      const t = targetSector ? String(targetSector) : '';
      if (!t) return 0;
      const idx = pages.findIndex((p) => p && p.plate === 'LANDING' && String(p.sector || '') === t);
      return idx >= 0 ? idx : 0;
    },
    [pages]
  );

  const onBackToSector = useCallback(() => {
    if (!page) return;
    const t = page.sector ? String(page.sector) : '';
    const landingIdx = findSectorLandingIndex(t);
    setIndex((prev) => clamp(landingIdx, 0, Math.max(0, pages.length - 1)));
  }, [findSectorLandingIndex, page, pages.length]);

  const isFullBleedTemplate = useMemo(() => {
    const templateName = page && page.template ? String(page.template) : '';
    const fullBleedSet = templatePack && templatePack.fullBleedTemplateNames ? templatePack.fullBleedTemplateNames : null;
    if (!templateName || !fullBleedSet || typeof fullBleedSet.has !== 'function') return false;
    return fullBleedSet.has(templateName);
  }, [page, templatePack]);

  const fullBleedHtml = useMemo(() => {
    if (!isFullBleedTemplate) return '';
    const templateName = page && page.template ? String(page.template) : '';
    const fn = templatePack && typeof templatePack.renderSlideTemplateHtml === 'function' ? templatePack.renderSlideTemplateHtml : null;
    if (!fn) return '';
    return fn({ templateName, item: page && page.item ? page.item : null, assetsBaseUrl });
  }, [assetsBaseUrl, isFullBleedTemplate, page, templatePack]);

  const resolvedImgUrl = useMemo(() => {
    if (!page || !page.img) return '';
    const resolver = templatePack && typeof templatePack.resolveDeckAssetUrl === 'function' ? templatePack.resolveDeckAssetUrl : null;
    if (resolver) return resolver(page.img, assetsBaseUrl);
    if (!page.img) return '';
    const s = String(page.img);
    if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')) return s;
    const base = assetsBaseUrl ? String(assetsBaseUrl) : '';
    if (!base) return s;
    if (base.endsWith('/')) return `${base}${s}`;
    return `${base}/${s}`;
  }, [page, assetsBaseUrl, templatePack]);

  const resolvedVideoUrl = useMemo(() => {
    return getVideoUrlFromImageUrl(resolvedImgUrl);
  }, [resolvedImgUrl]);

  const [preferVideo, setPreferVideo] = useState(true);

  useEffect(() => {
    setPreferVideo(true);
  }, [resolvedVideoUrl]);

  useEffect(() => {
    if (motionDisabled) setPreferVideo(false);
  }, [motionDisabled]);

  const changePage = useCallback(
    (delta) => {
      if (!pages || pages.length === 0) return;
      setIndex((prev) => clamp(prev + delta, 0, pages.length - 1));
    },
    [pages]
  );

  useSwipeNavigation({
    onSwipeLeft: () => changePage(1),
    onSwipeRight: () => changePage(-1)
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasTouch =
      'ontouchstart' in window ||
      (navigator && navigator.maxTouchPoints > 0) ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    if (!hasTouch) return;

    setShowSwipeHint(true);
    const hideHint = () => setShowSwipeHint(false);
    const onPointerDown = (e) => {
      if (!e) return;
      if (e.pointerType && e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      hideHint();
    };
    const timeoutId = window.setTimeout(hideHint, 3500);
    window.addEventListener('touchstart', hideHint, { passive: true, once: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true, once: true });

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('touchstart', hideHint);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (typeof registerNavHandlers === 'function' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        return;
      }
      if (e.key === 'ArrowLeft') changePage(-1);
      else if (e.key === 'ArrowRight') changePage(1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [changePage, registerNavHandlers]);

  useEffect(() => {
    if (typeof registerNavHandlers !== 'function') return;
    registerNavHandlers({
      prev: () => changePage(-1),
      next: () => changePage(1),
      back: onBackToSector,
      mit: null,
      state: {
        canPrev: !!(pages && pages.length > 0 && index > 0),
        canNext: !!(pages && pages.length > 0 && index < pages.length - 1),
        canBack: !!(page && page.sector),
        canMit: false
      }
    });
    return () => {
      try {
        registerNavHandlers(null);
      } catch (_e) {
      }
    };
  }, [changePage, index, onBackToSector, page, pages, registerNavHandlers]);

  const headerKicker = useMemo(() => {
    if (!page) return '';
    const parts = [];
    if (page.sector) parts.push(String(page.sector));
    if (page.kind) parts.push(String(page.kind));
    return parts.join(' • ');
  }, [page]);

  const slideScrubberBlocks = useMemo(() => {
    const blocks = [];
    if (!Array.isArray(pages) || pages.length === 0) return blocks;

    let currentSector = null;
    let sectorStart = 0;
    const sectors = [];

    for (let i = 0; i < pages.length; i++) {
      const p = pages[i];
      const sectorName = p && p.sector ? p.sector : '';
      if (sectorName !== currentSector) {
        if (currentSector !== null) {
          sectors.push({ sector: currentSector, start: sectorStart, end: i - 1 });
        }
        currentSector = sectorName;
        sectorStart = i;
      }
    }
    if (currentSector !== null) {
      sectors.push({ sector: currentSector, start: sectorStart, end: pages.length - 1 });
    }

    sectors.forEach((s, sectorIdx) => {
      const count = s.end - s.start + 1;
      const isYellow = sectorIdx % 2 === 0;
      for (let i = 0; i < count; i++) {
        const slideIdx = s.start + i;
        blocks.push({
          slideIdx,
          isYellow,
          isFirstInSector: i === 0,
          isFirstSector: sectorIdx === 0
        });
      }
    });

    return blocks;
  }, [pages]);

  const renderBody = () => {
    if (!page) return null;

    const paragraphSeed = getParagraphSeed(page);

    if (page.plate === 'LANDING') {
      const storyParts = page.text ? String(page.text).split(/\n\n+/).filter(Boolean) : [];
      return (
        <div className="pred-powerpoint-body">
          {storyParts.map((p, idx) => (
            <p key={idx} className="pred-powerpoint-landing-paragraph">{p}</p>
          ))}
        </div>
      );
    }

    if (page.plate === 'CTA' && page.cta && typeof page.cta === 'object') {
      const cta = page.cta;
      const bullets = Array.isArray(cta.bullets) ? cta.bullets : [];
      const steps = Array.isArray(cta.steps) ? cta.steps : [];

      return (
        <div className="pred-powerpoint-body">
          {cta.headline ? <p className="pred-powerpoint-paragraph">{String(cta.headline)}</p> : null}
          {bullets.length > 0 ? (
            <ul className="pred-powerpoint-list">
              {bullets.map((b, idx) => (
                <li key={idx}>{String(b)}</li>
              ))}
            </ul>
          ) : null}
          {steps.length > 0 ? (
            <ol className="pred-powerpoint-list ordered">
              {steps.map((s, idx) => (
                <li key={idx}>{String(s)}</li>
              ))}
            </ol>
          ) : null}
          {page.contact ? <p className="pred-powerpoint-paragraph">{String(page.contact)}</p> : null}
        </div>
      );
    }

    if (Array.isArray(page.actions) && page.actions.length > 0) {
      return (
        <div className="pred-powerpoint-body">
          <ul className="pred-powerpoint-list">
            {page.actions.map((a, idx) => (
              <li key={idx}>{String(a)}</li>
            ))}
          </ul>
        </div>
      );
    }

    if (page.html) {
      const htmlText = normalizeHtmlToText(page.html);
      const parsed = parseBulletContent(htmlText);

      if (parsed.type === 'bullets') {
        return (
          <div className="pred-powerpoint-body">
            {parsed.prefix.map((p, idx) => (
              <p key={`hp-${idx}`} className="pred-powerpoint-paragraph" dangerouslySetInnerHTML={{ __html: formatTextWithBold(p) }} />
            ))}
            <ul className="pred-powerpoint-list">
              {parsed.items.map((item, idx) => (
                <li key={`hl-${idx}`} dangerouslySetInnerHTML={{ __html: formatTextWithBold(item) }} />
              ))}
            </ul>
          </div>
        );
      }

      const paragraphs = createParagraphs(htmlText, paragraphSeed);
      return (
        <div className="pred-powerpoint-body">
          {paragraphs.map((p, idx) => (
            <p key={`ht-${idx}`} className="pred-powerpoint-paragraph" dangerouslySetInnerHTML={{ __html: formatTextWithBold(p) }} />
          ))}
        </div>
      );
    }

    if (page.paragraphs && Array.isArray(page.paragraphs)) {
      const combined = page.paragraphs.map((p) => String(p)).join(' ').replace(/\s+/g, ' ').trim();
      const split = combined ? createLegacyParagraphs(combined, paragraphSeed) : [];
      const paragraphs = split.length ? split : page.paragraphs;
      return (
        <div className="pred-powerpoint-body">
          {paragraphs.map((p, idx) => (
            <p key={idx} className="pred-powerpoint-paragraph" dangerouslySetInnerHTML={{ __html: formatTextWithBold(p) }} />
          ))}
        </div>
      );
    }

    const text = page.text || '';
    const parsed = parseBulletContent(text);
    
    if (parsed.type === 'bullets') {
      return (
        <div className="pred-powerpoint-body">
          {parsed.prefix.map((p, idx) => (
            <p key={`p-${idx}`} className="pred-powerpoint-paragraph" dangerouslySetInnerHTML={{ __html: formatTextWithBold(p) }} />
          ))}
          <ul className="pred-powerpoint-list">
            {parsed.items.map((item, idx) => (
              <li key={idx} dangerouslySetInnerHTML={{ __html: formatTextWithBold(item) }} />
            ))}
          </ul>
        </div>
      );
    }
    
    const paragraphs = createParagraphs(text, paragraphSeed);
    return (
      <div className="pred-powerpoint-body">
        {paragraphs.map((p, idx) => (
          <p key={idx} className="pred-powerpoint-paragraph" dangerouslySetInnerHTML={{ __html: formatTextWithBold(p) }} />
        ))}
      </div>
    );
  };

  return (
    <div className="pred-powerpoint-root">
      {dataState && dataState.status === 'loading' ? <div className="pred-powerpoint-loading">Loading slides…</div> : null}

      {(!dataState || dataState.status === 'ready') && (
        <>
          <div className="pred-powerpoint-stage">
            <div className={`pred-powerpoint-deck${isFullBleedTemplate ? ' fullbleed' : ''}`}>
              {isFullBleedTemplate ? (
                <div className="pred-powerpoint-fullbleed" dangerouslySetInnerHTML={{ __html: fullBleedHtml }} />
              ) : (
                <>
                  <div className="pred-powerpoint-media">
                    {preferVideo && !motionDisabled ? (
                      <video
                        className="pred-powerpoint-video"
                        src={resolvedVideoUrl}
                        muted
                        autoPlay
                        loop
                        playsInline
                        preload="metadata"
                        onError={() => setPreferVideo(false)}
                      />
                    ) : null}
                    {!preferVideo || motionDisabled ? (
                      <img className="pred-powerpoint-image" src={resolvedImgUrl} alt={page && page.title ? page.title : ''} />
                    ) : null}
                    <div className="pred-powerpoint-media-overlay" />
                  </div>

                  <div className="pred-powerpoint-text">
                    <div className="pred-powerpoint-content" ref={contentScrollRef}>
                      {page && (page.plate === 'LANDING' || page.plate === 'TITLE') ? (
                        <div className="pred-powerpoint-title-layout">
                          <h1 className="pred-powerpoint-big-title">{page && page.title ? page.title : ''}</h1>
                          {renderBody()}
                        </div>
                      ) : (
                        <>
                          <div className="pred-powerpoint-kicker">{headerKicker}</div>
                          <h1 className="pred-powerpoint-title">{page && page.title ? page.title : ''}</h1>
                          {page && page.plate ? (
                            <div className="pred-powerpoint-subheading">{String(page.plate)}</div>
                          ) : null}
                          {renderBody()}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="slide-scrubber">
            <div className={`pred-swipe-hint ${showSwipeHint ? 'show' : ''}`}>Swipe to navigate</div>
            <div className="slide-scrubber-label" style={{ textTransform: 'uppercase', letterSpacing: 2 }}>
              {page && page.sector ? page.sector : ''}
            </div>
            <div className="sector-blocks">
              {slideScrubberBlocks.map((b) => {
                const isActive = b.slideIdx === index;
                const background = isActive
                  ? (b.isYellow ? '#000' : 'var(--safety-yellow)')
                  : (b.isYellow ? 'var(--safety-yellow)' : '#000');

                return (
                  <div
                    key={b.slideIdx}
                    className={`sector-block${isActive ? ' active' : ''}`}
                    style={{
                      background,
                      marginLeft: b.isFirstInSector && !b.isFirstSector ? 20 : undefined
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIndex(b.slideIdx);
                    }}
                  />
                );
              })}
            </div>
            <div className="slide-scrubber-info">
              <span id="sliderCurrent">{String(Math.min(index + 1, pages.length)).padStart(2, '0')}</span> /{' '}
              <span id="sliderTotal">{String(pages.length).padStart(2, '0')}</span>
            </div>
          </div>
        </>
      )}

      {dataState && dataState.status === 'error' ? (
        <div className="pred-powerpoint-error">
          Failed to preload deck data: {String(dataState.error && dataState.error.message ? dataState.error.message : dataState.error)}
        </div>
      ) : null}
    </div>
  );
}

export default PowerpointView;
